import React, { useEffect, useState } from "react";
import {
  Card,
  Button,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  List,
  Spin,
  Alert,
  Empty,
} from "antd";
import {
  CheckCircleOutlined,
  StarOutlined,
  ThunderboltOutlined,
  SafetyOutlined,
  CrownOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import api from "utils/api";
import Stepper from "assets/components/Stepper";

const { Title, Text, Paragraph } = Typography;

const getIcon = (text, fallback, highlight = false) => {
  const iconMap = {
    "5-Second AI Response": (
      <ThunderboltOutlined style={{ color: "#0EA5E9", fontSize: "16px" }} />
    ),
    "Unified Lead Dashboard": (
      <ThunderboltOutlined style={{ color: "#0EA5E9", fontSize: "16px" }} />
    ),
    "Forwarding Phone": (
      <ThunderboltOutlined style={{ color: "#0EA5E9", fontSize: "16px" }} />
    ),
    "System Credits": (
      <ThunderboltOutlined style={{ color: "#0EA5E9", fontSize: "16px" }} />
    ),
    "Web Form": (
      <ThunderboltOutlined style={{ color: "#0EA5E9", fontSize: "16px" }} />
    ),
    "Email Alerts": (
      <ThunderboltOutlined style={{ color: "#0EA5E9", fontSize: "16px" }} />
    ),
    "Customizable Response": (
      <ThunderboltOutlined style={{ color: "#0EA5E9", fontSize: "16px" }} />
    ),
    Video: (
      <ThunderboltOutlined style={{ color: "#6366F1", fontSize: "16px" }} />
    ),
    "After-Hours": (
      <ThunderboltOutlined style={{ color: "#6366F1", fontSize: "16px" }} />
    ),
    SMS: <ThunderboltOutlined style={{ color: "#6366F1", fontSize: "16px" }} />,
  };
  const key = Object.keys(iconMap).find((k) => text.includes(k));
  return (
    iconMap[key] || (
      <CheckCircleOutlined
        style={{ color: highlight ? "#A78BFA" : "#10B981", fontSize: "16px" }}
      />
    )
  );
};

// Helper function to render feature lists
const renderFeatures = (featuresArray, isPremiumCard = false) => {
  if (!featuresArray || featuresArray.length === 0) {
    return null;
  }
  const validFeatures = featuresArray.filter(
    (feature) => typeof feature === "string"
  );
  if (validFeatures.length === 0) {
    return null;
  }
  return (
    <List
      size="small"
      dataSource={validFeatures}
      renderItem={(feature) => (
        <List.Item style={{ padding: "6px 0" }}>
          <Space size="middle">
            <CheckCircleOutlined
              style={{
                color: isPremiumCard ? "#ffffff" : "#10B981",
                fontSize: "10px",
              }}
            />
            <Text
              style={{
                color: isPremiumCard ? "#FFFFFF" : "#1F2937",
                fontSize: "10px",
                fontWeight: 500,
                lineHeight: 1.5,
              }}
            >
              {feature}
            </Text>
          </Space>
        </List.Item>
      )}
    />
  );
};

const PricingCard = ({ plan }) => {
  const navigate = useNavigate();
  const isPremium = plan.highlight;

  const getPlanIcon = () => {
    if (isPremium)
      return <CrownOutlined style={{ fontSize: "24px", color: "#F59E0B" }} />;
    if (plan.name.toLowerCase().includes("basic"))
      return <SafetyOutlined style={{ fontSize: "24px", color: "#3B82F6" }} />;
    return <RocketOutlined style={{ fontSize: "24px", color: "#8B5CF6" }} />;
  };

  const getPlanColor = () => {
    if (isPremium) return "#7C3AED"; // Purple
    if (plan.name.toLowerCase().includes("basic")) return "#3B82F6"; // Blue
    return "#8B5CF6"; // Indigo
  };

  const handleCtaClick = () => {
    // Save the selected plan's ID to session storage for the next step
    sessionStorage.setItem("selectedPlanId", plan._id);
    navigate(`/checkout/${plan.name.toLowerCase()}`);
  };

  return (
    <Col xs={24} md={12} lg={8} xl={6}>
      <Card
        className={`h-full transform transition-all duration-300 hover:scale-105 ${
          isPremium ? "ring-2 ring-purple-400 ring-opacity-50" : ""
        }`}
        style={{
          background: isPremium
            ? "linear-gradient(135deg, #7C3AED 0%, #6366F1 100%)"
            : "#FFFFFF",
          borderColor: isPremium ? "#7C3AED" : "#E5E7EB",
          boxShadow: isPremium
            ? "0 25px 50px -12px rgba(124, 58, 237, 0.25)"
            : "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
          borderRadius: "16px",
        }}
        bodyStyle={{
          padding: "20px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isPremium && plan.badgeText && (
          <div className="text-center mb-6">
            <Tag
              icon={<StarOutlined />}
              color="#F59E0B"
              style={{
                fontSize: "12px",
                padding: "10px 20px",
                borderRadius: "25px",
                border: "none",
                fontWeight: 600,
              }}
            >
              {plan.badgeText}
            </Tag>
          </div>
        )}

        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">{getPlanIcon()}</div>
          <Title
            level={2}
            style={{
              color: isPremium ? "#FFFFFF" : "#1F2937",
              marginBottom: "12px",
              fontSize: "16px",
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {plan.name}
          </Title>
          <Paragraph
            style={{
              color: isPremium ? "rgba(255, 255, 255, 0.9)" : "#6B7280",
              margin: 0,
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: 1.5,
            }}
          >
            {plan.description}
          </Paragraph>
        </div>

        <div className="text-center mb-10">
          <div className="flex items-baseline justify-center">
            <Text
              style={{
                fontSize: "24px",
                fontWeight: 800,
                color: isPremium ? "#FFFFFF" : "#1F2937",
                lineHeight: 1,
                marginRight: "8px",
              }}
            >
              ${plan.price}
            </Text>
            <Text
              style={{
                fontSize: "14px",
                color: isPremium ? "rgba(255, 255, 255, 0.8)" : "#6B7280",
                fontWeight: 500,
              }}
            >
              {plan.priceSuffix}
            </Text>
          </div>
        </div>

        <div className="flex-grow mb-10">
          {plan.features && plan.features.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <ThunderboltOutlined
                  style={{
                    color: isPremium ? "#FBBF24" : getPlanColor(),
                    marginRight: "12px",
                    fontSize: "20px",
                  }}
                />
                <Title
                  level={4}
                  style={{
                    color: isPremium ? "#FFFFFF" : "#374151",
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: 600,
                    lineHeight: 1.3,
                  }}
                >
                  Included Features
                </Title>
              </div>
              {renderFeatures(
                plan.features.map((f) => f.text),
                isPremium
              )}
            </div>
          )}

          {plan.callInsights && plan.callInsights.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <ThunderboltOutlined
                  style={{
                    color: isPremium ? "#FBBF24" : getPlanColor(),
                    marginRight: "12px",
                    fontSize: "20px",
                  }}
                />
                <Title
                  level={4}
                  style={{
                    color: isPremium ? "#FFFFFF" : "#374151",
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: 600,
                    lineHeight: 1.3,
                  }}
                >
                  Call Insights & Reporting
                </Title>
              </div>
              {renderFeatures(
                plan.callInsights.map((f) => f.text),
                isPremium
              )}
            </div>
          )}
        </div>

        <Button
          type="primary"
          size="large"
          block
          onClick={handleCtaClick}
          style={{
            height: "30px",
            fontSize: "12px",
            fontWeight: 600,
            borderRadius: "12px",
            boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
            border: "none",
            background: isPremium ? "#FFFFFF" : getPlanColor(),
            borderColor: isPremium ? "#FFFFFF" : getPlanColor(),
            color: isPremium ? getPlanColor() : "#FFFFFF",
          }}
          className="hover:shadow-lg transition-all duration-300"
        >
          {plan.ctaText}
        </Button>
      </Card>
    </Col>
  );
};

const PricingPage = () => {
  const [plansData, setPlansData] = useState([]);
  const [pendingUserData, setPendingUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const storedDataString = sessionStorage.getItem("pendingUserData");
    if (!storedDataString) {
      console.warn("No pending user data found. Redirecting to signup.");
      navigate("/signup");
      return;
    }

    setPendingUserData(JSON.parse(storedDataString));

    const fetchPlans = async () => {
      try {
        const response = await api.get("/plan");
        if (response.data.success && Array.isArray(response.data.content)) {
          const transformed = response.data.content.map((plan) => {
            const features = [];
            let callInsights = [];

            if (Array.isArray(plan.features)) {
              plan.features.forEach((group) => {
                const title =
                  typeof group.title === "string" ? group.title : "";
                const isInsight = title.toLowerCase().includes("call insight");
                if (Array.isArray(group.features)) {
                  group.features.forEach((item) => {
                    if (typeof item === "string") {
                      const entry = {
                        text: item,
                        icon: getIcon(
                          item,
                          null,
                          plan.name.includes("Premium")
                        ),
                      };
                      isInsight
                        ? callInsights.push(entry)
                        : features.push(entry);
                    }
                  });
                }
              });
            }

            const isPremium = plan.name.includes("Premium");
            return {
              ...plan,
              priceSuffix: "/Month",
              description:
                plan.features?.[0]?.title || "A great plan to get you started.",
              features,
              callInsights,
              ctaText: "Select Plan",
              highlight: isPremium,
              bgColor: isPremium ? "bg-indigo-600" : "bg-white",
              borderColor: isPremium ? "border-indigo-700" : "border-gray-200",
              textColor: isPremium ? "text-white" : undefined,
              featureTextColor: isPremium ? "text-indigo-100" : undefined,
              descriptionColor: isPremium ? "text-indigo-200" : undefined,
              priceColor: isPremium ? "text-white" : undefined,
              buttonClass: isPremium
                ? "bg-white hover:bg-indigo-50 text-indigo-600"
                : "bg-sky-500 hover:bg-sky-600 text-white",
              badgeText: isPremium ? "Most Popular" : null,
            };
          });
          // Sort plans by price from lowest to highest
          const sortedPlans = transformed.sort((a, b) => a.price - b.price);
          setPlansData(sortedPlans);
        }
      } catch (error) {
        console.error("Failed to fetch plans:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlans();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <Spin size="large" />
        <Title
          level={3}
          style={{
            color: "#000000ff",
            marginTop: "24px",
            fontSize: "24px",
            fontWeight: 600,
          }}
        >
          Verifying session...
        </Title>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full py-16 md:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        <Stepper currentStep={2} />

        <div className="text-center my-6 md:my-6">
          <Title
            level={1}
            style={{
              color: "#000000ff",
              fontSize: "24px",
              fontWeight: 800,
              marginBottom: "20px",
              lineHeight: 1.1,
            }}
          >
            Choose the <span style={{ color: "#A78BFA" }}>Perfect Plan</span>{" "}
            For You
          </Title>
          <Paragraph
            style={{
              color: "#000000ff",
              fontSize: "14px",
              maxWidth: "700px",
              margin: "0 auto",
              fontWeight: 500,
              lineHeight: 1.6,
            }}
          >
            Hello, {pendingUserData?.name}! You're almost there. Choose a plan
            that suits your needs.
          </Paragraph>
        </div>

        <Row gutter={[32, 32]} justify="center">
          {plansData.map((plan) => (
            <PricingCard key={plan._id} plan={plan} />
          ))}
        </Row>

        <div className="text-center mt-20 md:mt-24">
          <Text
            style={{ color: "#000000ff", fontSize: "14px", fontWeight: 500 }}
          >
            Have questions?{" "}
            <a href="#" className="text-sky-400 hover:text-sky-300 font-medium">
              Contact Us
            </a>
            .
          </Text>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
